import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

const ROLE_OPTIONS = ["Admin", "Supervisor", "Guard", "Manager"];
const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "disapproved", label: "Disapproved" },
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  designation: "",
  role: "",
  status: "pending",
  username: "",
  password: "",
  guard_seq: "",
  memberId: "",
};

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function AddUser() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const isEdit = params.get("mode") === "edit";
  const memberIdFromQuery = params.get("member_id") || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [form, setForm] = useState(initialForm);
  const [guards, setGuards] = useState([]);
  const [staffDbConnected, setStaffDbConnected] = useState(true);
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState({});
  const [selectedSubmodules, setSelectedSubmodules] = useState({});
  const [licensedUsers, setLicensedUsers] = useState(0);
  const [currentUsers, setCurrentUsers] = useState(0);
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  const normalizeGuards = (payload) => {
    const list =
      payload?.guards ||
      payload?.staff ||
      payload?.payrollStaff ||
      payload?.data?.guards ||
      [];

    return Array.isArray(list) ? list : [];
  };

  const fetchPageData = async (local, token) => {
    try {
      const [dashboardRes, formRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        fetch(
          `${API_BASE_URL}/users/form-init${
            isEdit ? `?mode=edit&member_id=${encodeURIComponent(memberIdFromQuery)}` : ""
          }`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!formRes.ok) throw new Error("Failed to fetch user form data");

      const dashboardResult = await dashboardRes.json();
      const formResult = await formRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const payload = formResult?.data || {};

      const guardList = normalizeGuards(payload);
      console.log("Payroll guards from /users/form-init:", guardList);

      setLicensedUsers(Number(payload.licensedUsers || 0));
      setCurrentUsers(Number(payload.currentUsers || 0));
      setLimitReached(!!payload.limitReached);
      setGuards(guardList);
      setStaffDbConnected(payload.staffDbConnected !== false);

      const modulesWithSubs = (payload.modules || []).map((m) => ({
        ...m,
        page_id: String(m.page_id),
        submodules: payload.subpagesByPage?.[String(m.page_id)] || m.submodules || [],
      }));

      setModules(modulesWithSubs);

      const vm = payload.vm || {};

      setForm({
        ...initialForm,
        fullName: vm.fullName || "",
        email: vm.email || "",
        phone: vm.phone || "",
        designation: vm.designation || "",
        role: vm.role || "",
        status: vm.status || "pending",
        username: vm.username || "",
        password: "",
        guard_seq: vm.guard_seq === null || vm.guard_seq === undefined ? "" : String(vm.guard_seq),
        memberId: vm.memberId || "",
      });

      const existingPermissions = payload.existingPermissions || {};
      const modulesState = {};
      const submodulesState = {};

      modulesWithSubs.forEach((module) => {
        const pageId = String(module.page_id);
        const permission = existingPermissions?.[pageId];
        const enabled = !!permission?.main_access;

        modulesState[pageId] = enabled;
        submodulesState[pageId] = enabled ? Object.keys(permission?.submodules || {}) : [];
      });

      setSelectedModules(modulesState);
      setSelectedSubmodules(submodulesState);
    } catch (error) {
      console.error("Add user fetch error:", error);
      toast.error(error.message || "Failed to load page data.");
    } finally {
      setLoading(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const remainingUsers = useMemo(() => {
    if (licensedUsers > 0) return Math.max(0, licensedUsers - currentUsers);
    return null;
  }, [licensedUsers, currentUsers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleModule = (pageId) => {
    const key = String(pageId);

    setSelectedModules((prev) => {
      const next = !prev[key];

      if (!next) {
        setSelectedSubmodules((old) => ({ ...old, [key]: [] }));
      }

      return { ...prev, [key]: next };
    });
  };

  const toggleSubmodule = (pageId, submoduleName) => {
    const key = String(pageId);
    if (!selectedModules[key]) return;

    setSelectedSubmodules((prev) => {
      const current = prev[key] || [];
      const exists = current.includes(submoduleName);

      return {
        ...prev,
        [key]: exists
          ? current.filter((item) => item !== submoduleName)
          : [...current, submoduleName],
      };
    });
  };

  const buildPayload = () => {
    const perm_module = {};
    const perm_sub = {};

    Object.entries(selectedModules).forEach(([pageId, enabled]) => {
      if (enabled) {
        perm_module[pageId] = true;
        perm_sub[pageId] = selectedSubmodules[pageId] || [];
      }
    });

    return {
      form_mode: isEdit ? "edit" : "create",
      fullName: form.fullName,
      email: form.email,
      phone: form.phone,
      designation: form.designation,
      role: form.role,
      status: form.status,
      username: form.username,
      password: form.password,
      guard_seq: form.guard_seq === "" ? null : Number(form.guard_seq),
      memberId: form.memberId,
      perm_module,
      perm_sub,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.fullName.trim() || !form.username.trim() || !form.role.trim()) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (!isEdit && limitReached) {
      toast.error(`User limit reached (${currentUsers} of ${licensedUsers}). You cannot add more users.`);
      return;
    }

    try {
      setSaving(true);

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}/users/${isEdit ? "update" : "create"}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(buildPayload()),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save user");
      }

      toast.success(isEdit ? "User updated successfully!" : "User created successfully!");

      setTimeout(() => {
        navigate("/user_list");
      }, 1000);
    } catch (error) {
      console.error("Save user error:", error);
      toast.error(error.message || "Failed to save user.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading add user page...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="add_user"
      activeMenu="users"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />
      
      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        {/* Page Header */}
        <div className="mb-1">
          <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
            User Management
          </h1>
          <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
            {isEdit ? "Update existing user details and permissions" : "Create a new user and assign permissions"}
          </p>
        </div>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px] mt-[22px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                {isEdit ? "Edit User" : "Add New User"}
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Fill the form below to {isEdit ? "update the account" : "create the account"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/user_list")}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Back to User List
            </button>
          </div>
        </div>

        {/* License Banner */}
        <div
          className={`mb-[18px] px-4 py-3.5 rounded-xl text-[13px] font-bold border ${
            limitReached
              ? "bg-[var(--red-faint)] border-[var(--red-border)] text-[var(--red)]"
              : "bg-[rgba(34,197,94,0.12)] border-[rgba(34,197,94,0.28)] text-[#22c55e]"
          }`}
        >
          <span className="font-extrabold">User Capacity:</span>{" "}
          {licensedUsers > 0 ? `${currentUsers} / ${licensedUsers}` : `${currentUsers} (no limit set)`}
          {" · "}
          {limitReached
            ? "Limit reached — new user creation is disabled."
            : licensedUsers > 0
            ? `You can add ${remainingUsers} user(s).`
            : "You can add more user(s)."}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <div className="mb-3.5">
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Basic Information
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                {isEdit ? "Modify user profile details" : "Enter the user's profile details"}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Full Name <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <input
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Phone
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Designation
                </label>
                <input
                  name="designation"
                  value={form.designation}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder="Enter designation"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Role <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <select
                  name="role"
                  value={form.role}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                >
                  <option value="">Select role</option>
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Status
                </label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Login Credentials */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <div className="mb-3.5">
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Login Credentials
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Set username, password, and payroll staff mapping
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Username <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <input
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder="Enter username"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  {isEdit ? "Password (leave blank to keep current)" : "Password *"}
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  required={!isEdit}
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  placeholder={isEdit ? "Leave blank to keep current" : "Enter password"}
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Payroll Staff / Guard
                </label>
                <select
                  name="guard_seq"
                  value={form.guard_seq}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                >
                  <option value="">-- Select Payroll Staff --</option>
                  {!staffDbConnected ? (
                    <option value="" disabled>Payroll DB connection failed</option>
                  ) : guards.length === 0 ? (
                    <option value="" disabled>No payroll staff found</option>
                  ) : (
                    guards.map((g, index) => {
                      const seq = g.staff_seq ?? g.staffSeq ?? g.seq ?? g.id ?? g.staff_id ?? "";
                      const name = g.staff_name || g.staffName || g.name || g.employee_name || g.full_name || "";
                      const applicationNumber = g.application_number || g.applicationNumber || g.app_no || g.appNo || "";
                      const label = `${name}${applicationNumber ? ` (${applicationNumber})` : ""}`;
                      return (
                        <option key={seq || index} value={String(seq)}>
                          {label || `Staff ${index + 1}`}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Member ID
                </label>
                <input
                  name="memberId"
                  value={form.memberId}
                  readOnly
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                  placeholder="Auto-generated"
                />
              </div>
            </div>
          </div>

          {/* Module Access */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
            <div className="mb-3.5">
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Module Access & Permissions
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Toggle modules and choose submodule access
              </p>
            </div>

            <div className="space-y-3.5">
              {modules.length === 0 ? (
                <div className="py-4 px-4 rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] font-semibold text-center">
                  No modules found.
                </div>
              ) : (
                modules.map((module) => {
                  const pageId = String(module.page_id);
                  const enabled = !!selectedModules[pageId];
                  const selectedSubs = selectedSubmodules[pageId] || [];
                  const subs = module.submodules || [];

                  return (
                    <div key={pageId} className="border border-[var(--surface-border)] rounded-xl overflow-hidden bg-[var(--card-bg)]">
                      <div className="px-5 py-4 flex justify-between items-center gap-3 flex-wrap" style={{ background: "var(--btn-bg)" }}>
                        <div className="text-[15px] font-extrabold text-[var(--btn-text)]">
                          {module.modules}
                        </div>
                        <label className="flex items-center gap-2.5 text-[12px] font-bold text-[var(--btn-text)] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => toggleModule(pageId)}
                            className="w-[18px] h-[18px] accent-[var(--accent)] cursor-pointer"
                          />
                          Enable Module
                        </label>
                      </div>

                      <div className="p-5">
                        {subs.length === 0 ? (
                          <div className="py-4 px-4 rounded-xl border border-dashed border-[var(--surface-border)] text-[var(--text-muted)] text-[13px] font-semibold text-center">
                            No sub-modules. Enabling grants full access.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
                            {subs.map((sub) => {
                              const checked = selectedSubs.includes(sub.submodules);
                              return (
                                <label
                                  key={`${pageId}-${sub.submodules}`}
                                  className={`flex items-start gap-2.5 border border-[var(--surface-border)] rounded-xl p-3 ${
                                    enabled
                                      ? "bg-[var(--surface)] cursor-pointer hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                                      : "bg-[var(--surface)] opacity-50 cursor-not-allowed"
                                  } transition-colors duration-150`}
                                >
                                  <input
                                    type="checkbox"
                                    disabled={!enabled}
                                    checked={checked}
                                    onChange={() => toggleSubmodule(pageId, sub.submodules)}
                                    className="mt-1 w-4 h-4 accent-[var(--accent)] flex-shrink-0 disabled:cursor-not-allowed"
                                  />
                                  <div>
                                    <div className="text-[13px] font-bold text-[var(--text-primary)]">
                                      {sub.submodules}
                                    </div>
                                    {sub.links && (
                                      <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">
                                        {sub.links}
                                      </div>
                                    )}
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 flex-wrap mt-1.5 mb-3">
            <button
              type="button"
              onClick={() => navigate("/user_list")}
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
            >
              Back to User List
            </button>
            <button
              type="submit"
              disabled={saving || (!isEdit && limitReached)}
              className="border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              {saving ? "Saving..." : isEdit ? "Update User" : "+ Create User"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}